import { forwardRef } from 'react';

import { CAST_AI } from '@cast/constants';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerProps,
  useDrawerInstance,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import { Link } from 'components/router';
import { useGetClusterEdgeLocationsQuery } from 'hooks/queries/omni';

import { edgeLocationsCriteria } from '../../constants';
import { EdgeLocationsFilters } from '../../EdgeLocations/EdgeLocationsFilters';
import { EdgeLocationsTable } from '../../EdgeLocations/EdgeLocationsTable';

import { SearchProvider } from '@cast/shared-search-ui';

export type EdgeLocationsListDrawerProps = DrawerProps & {
  clusterId: string;
};

export const EdgeLocationsListDrawer = forwardRef<
  HTMLDivElement,
  EdgeLocationsListDrawerProps
>(({ clusterId, ...props }, ref) => {
  const { close } = useDrawerInstance();
  const { edgeLocations, isLoading, error } = useGetClusterEdgeLocationsQuery({
    clusterId,
    includeNodeTemplates: true,
  });

  return (
    <Drawer
      ref={ref}
      testId="edge-locations-list-drawer"
      size="large"
      {...props}
    >
      <DrawerHeader sx={{ justifyContent: 'space-between' }}>
        <Button
          testId="manage-edge-locations-button"
          variant="ghost"
          size="sm"
          component={Link}
          to={`/automation/external-clusters/${clusterId}/autoscaler/settings/edge-locations`}
          onClick={() => close()}
        >
          Manage edge locations
        </Button>
      </DrawerHeader>
      <DrawerBody sx={{ height: '100%' }}>
        <DrawerContent gap={24} flexGrow={1}>
          <Stack gap={8}>
            <Typography
              variant="L12R"
              color={tokens.theme.fg.structural.secondary}
            >
              Extended regions
            </Typography>
            <Typography variant="h5" mb={8}>
              Edge locations
            </Typography>
            <Typography variant="P14R" color="textSecondary">
              Edge locations extend your cluster to other regions or clouds.
              When capacity isn&apos;t available in the main region, {CAST_AI}{' '}
              can automatically provision nodes in these locations to keep your
              workloads running.
            </Typography>
          </Stack>
          <SearchProvider
            criteria={edgeLocationsCriteria}
            data={edgeLocations}
            isLoading={isLoading}
            error={error}
          >
            <EdgeLocationsFilters showStateFilter={false} />
            <Stack mx={-32} flexGrow={1}>
              <EdgeLocationsTable showFullTable={false} />
            </Stack>
          </SearchProvider>
        </DrawerContent>
      </DrawerBody>
    </Drawer>
  );
});

EdgeLocationsListDrawer.displayName = 'EdgeLocationsListDrawer';
